/**
 * Scarica e tiene aggiornata la cache dei media.
 *  - Download SEQUENZIALI, uno alla volta, nell'ordine di priorità dato da `setTargets`
 *    (chi chiama mette prima le voci attive ora, poi il fallback, poi le successive).
 *  - Streaming verso un file temporaneo, sha256 incrementale, commit (rename atomico) solo se coincide.
 *  - Errore o sha diverso: si scarta e si riprova più tardi con backoff esponenziale (5 s -> 5 min),
 *    passando intanto agli asset successivi.
 *  - 403/401/410 sull'URL (firmato, scaduto): `onUrlProblem` -> il chiamante richiede il manifest
 *    senza If-None-Match per avere URL nuovi.
 *  - Non aspetta mai la rete per riprodurre: `isReady` è sincrono.
 */
import { backoffMs } from '../net/api';
import {
  keyString, type AssetKey, type AssetStore, type StorageEstimate,
} from './asset-cache';
import { isLowDisk, planGc } from './gc';
import { createHasher, type Hasher } from './sha256';

export interface DownloadTarget {
  id: string;
  url: string;
  sha256: string;
  size: number;
  mime: string;
}

export interface AssetManagerDeps {
  store: AssetStore;
  fetchImpl?: typeof fetch;
  now?: () => number;
  /** Un asset è diventato disponibile o è stato rimosso. */
  onChange?: () => void;
  onUrlProblem?: () => void;
  onError?: (msg: string) => void;
  loadUnreferenced?: () => Promise<Record<string, number>>;
  saveUnreferenced?: (v: Record<string, number>) => Promise<void>;
  makeHasher?: (size: number) => Hasher;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (h: unknown) => void;
}

class DownloadError extends Error {
  constructor(message: string, readonly urlProblem = false) { super(message); }
}

export class AssetManager {
  private readonly store: AssetStore;
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;
  private readonly makeHasher: (size: number) => Hasher;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (h: unknown) => void;

  private readonly ready = new Map<string, number>(); // chiave -> dimensione
  private targets: DownloadTarget[] = [];
  private referenced = new Set<string>();
  private readonly attempts = new Map<string, number>();
  private readonly retryAt = new Map<string, number>();
  private running = false;
  private wakeTimer: unknown = null;
  private epoch = 0; // incrementa su clearAll(): scarta i download in volo
  private current: string | null = null;
  lastError: string | null = null;
  bytesDownloaded = 0;

  constructor(private readonly deps: AssetManagerDeps) {
    this.store = deps.store;
    this.fetchImpl = deps.fetchImpl ?? ((i, o) => fetch(i, o));
    this.now = deps.now ?? (() => Date.now());
    this.makeHasher = deps.makeHasher ?? ((size) => createHasher(size));
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  /** Legge cosa c'è già su disco (così si parte subito offline). */
  async init(): Promise<void> {
    this.ready.clear();
    for (const s of await this.store.list()) this.ready.set(keyString(s), s.size);
  }

  isReady(id: string, sha256: string): boolean {
    return this.ready.has(`${id}|${sha256}`);
  }

  readyIds(): string[] {
    return [...new Set([...this.ready.keys()].map((k) => k.slice(0, k.lastIndexOf('|'))))];
  }

  get downloading(): string | null { return this.current; }

  pendingIds(): string[] {
    return this.targets.filter((t) => !this.isReady(t.id, t.sha256)).map((t) => t.id);
  }

  /** URL blob: del file su disco. Chi lo riceve fa `URL.revokeObjectURL`. */
  getObjectUrl(id: string, sha256: string): Promise<string> {
    return this.store.getObjectUrl({ id, sha256 });
  }

  /**
   * Imposta cosa scaricare, in ordine di priorità, e quali chiavi il GC deve considerare
   * referenziate (manifest in uso + manifest in attesa).
   */
  setTargets(targets: DownloadTarget[], referenced: AssetKey[]): void {
    const seen = new Set<string>();
    this.targets = targets.filter((t) => {
      const k = `${t.id}|${t.sha256}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    this.referenced = new Set(referenced.map(keyString));
    for (const k of [...this.retryAt.keys()]) if (!seen.has(k)) { this.retryAt.delete(k); this.attempts.delete(k); }
    this.kick();
  }

  /** Cancella tutto (comando clear_cache / 401) e riparte a scaricare i target correnti. */
  async clearAll(): Promise<void> {
    this.epoch++;
    this.ready.clear();
    this.attempts.clear();
    this.retryAt.clear();
    await this.store.clear();
    this.deps.onChange?.();
    this.kick();
  }

  /** Dopo un nuovo manifest con URL freschi: azzera i ritardi e riprova subito. */
  retryNow(): void {
    this.retryAt.clear();
    this.kick();
  }

  async estimate(): Promise<StorageEstimate | null> { return this.store.estimate(); }

  async gc(force = false): Promise<number> {
    const stored = await this.store.list();
    const est = await this.store.estimate();
    const plan = planGc({
      stored,
      referenced: this.referenced,
      unreferencedSince: (await this.deps.loadUnreferenced?.()) ?? {},
      now: this.now(),
      lowDisk: force || isLowDisk(est),
    });
    for (const k of plan.remove) {
      await this.store.remove(k);
      this.ready.delete(keyString(k));
    }
    await this.deps.saveUnreferenced?.(plan.unreferencedSince);
    if (plan.remove.length) this.deps.onChange?.();
    return plan.remove.length;
  }

  // --- loop di download -------------------------------------------------

  private kick(): void {
    if (this.wakeTimer !== null) { this.clearTimer(this.wakeTimer); this.wakeTimer = null; }
    if (this.running) return;
    this.running = true;
    void this.loop().finally(() => { this.running = false; });
  }

  private async loop(): Promise<void> {
    for (;;) {
      const t0 = this.now();
      const todo = this.targets.filter((t) => !this.isReady(t.id, t.sha256));
      if (todo.length === 0) return;
      const due = todo.find((t) => (this.retryAt.get(`${t.id}|${t.sha256}`) ?? 0) <= t0);
      if (!due) {
        const soonest = Math.min(...todo.map((t) => this.retryAt.get(`${t.id}|${t.sha256}`) ?? 0));
        this.wakeTimer = this.setTimer(() => { this.wakeTimer = null; this.kick(); }, Math.max(100, soonest - t0));
        return;
      }
      const key = `${due.id}|${due.sha256}`;
      const myEpoch = this.epoch;
      this.current = due.id;
      try {
        await this.download(due, myEpoch);
        this.attempts.delete(key);
        this.retryAt.delete(key);
        this.deps.onChange?.();
      } catch (e) {
        if (myEpoch !== this.epoch) continue; // annullato da clearAll(): nessuna penalità, si riparte
        const n = (this.attempts.get(key) ?? 0) + 1;
        this.attempts.set(key, n);
        this.retryAt.set(key, this.now() + backoffMs(n - 1));
        const msg = `download ${due.id}: ${e instanceof Error ? e.message : String(e)}`;
        this.lastError = msg;
        this.deps.onError?.(msg);
        if (e instanceof DownloadError && e.urlProblem) this.deps.onUrlProblem?.();
        if (e instanceof Error && e.name === 'QuotaExceededError') await this.gc(true).catch(() => undefined);
      } finally {
        this.current = null;
      }
    }
  }

  private async download(t: DownloadTarget, epoch: number): Promise<void> {
    let res: Response;
    try {
      res = await this.fetchImpl(t.url, { cache: 'no-store', credentials: 'omit' });
    } catch (e) {
      throw new DownloadError(`rete: ${e instanceof Error ? e.message : String(e)}`);
    }
    if (res.status === 401 || res.status === 403 || res.status === 410) throw new DownloadError(`HTTP ${res.status} (URL scaduto?)`, true);
    if (!res.ok) throw new DownloadError(`HTTP ${res.status}`);

    const key: AssetKey = { id: t.id, sha256: t.sha256 };
    const writer = await this.store.begin(key, { mime: t.mime, size: t.size });
    const hasher = this.makeHasher(t.size);
    let bytes = 0;
    try {
      if (res.body) {
        const reader = res.body.getReader();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          if (epoch !== this.epoch) { await reader.cancel().catch(() => undefined); throw new DownloadError('annullato'); }
          bytes += value.length;
          hasher.update(value);
          await writer.write(value);
        }
      } else { // browser senza streaming: costretto a leggere tutto
        const buf = new Uint8Array(await res.arrayBuffer());
        bytes = buf.length;
        hasher.update(buf);
        await writer.write(buf);
      }
      if (t.size > 0 && bytes !== t.size) throw new DownloadError(`dimensione ${bytes} != ${t.size}`);
      const hex = await hasher.hex();
      if (hex !== t.sha256) throw new DownloadError('sha256 diverso: file scartato');
      if (epoch !== this.epoch) throw new DownloadError('annullato');
      await writer.commit();
    } catch (e) {
      await writer.abort().catch(() => undefined);
      throw e;
    }
    this.bytesDownloaded += bytes;
    this.ready.set(`${t.id}|${t.sha256}`, bytes);
  }
}
